import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/providers/AuthProvider';
import { ArrowLeftOnRectangleIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export function Login() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const emailRequiredErrorMessage = 'Email es requerido';
  const passwordRequiredErrorMessage = 'Contraseña es requerida';
  const isEmailRequiredError = Boolean(error === emailRequiredErrorMessage && !email.trim());
  const isPasswordRequiredError = Boolean(error === passwordRequiredErrorMessage && !password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Detectar autofill del navegador: React no recibe onChange en autofill
    const form = e.target as HTMLFormElement;
    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    const passwordInput = form.querySelector<HTMLInputElement>('input[name="password"]');
    const actualEmail = emailInput?.value || email;
    const actualPassword = passwordInput?.value || password;
    if (actualEmail !== email) setEmail(actualEmail);
    if (actualPassword !== password) setPassword(actualPassword);

    const trimmedEmail = actualEmail?.trim() ?? '';
    if (!trimmedEmail) {
      setError(emailRequiredErrorMessage);
      return;
    }
    if (!actualPassword) {
      setError(passwordRequiredErrorMessage);
      return;
    }

    try {
      await login(actualEmail, actualPassword);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-secondary/10 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-3xl text-center text-emphasis-900 flex items-center justify-center gap-2">
            <ArrowLeftOnRectangleIcon width={32} height={32} className="text-primary" />
            Iniciar Sesión
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className={isEmailRequiredError ? 'mt-1 border-red-500 focus-visible:ring-red-500' : 'mt-1'}
                disabled={isLoading}
                required
                aria-required="true"
                aria-invalid={isEmailRequiredError}
                aria-describedby={isEmailRequiredError ? 'login-email-error' : undefined}
                autoComplete="email"
              />
              {isEmailRequiredError && (
                <p id="login-email-error" className="text-sm text-red-600 mt-1" role="alert">
                  Email es requerido
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password">Contraseña <span className="text-red-500">*</span></Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={isPasswordRequiredError ? 'mt-1 border-red-500 focus-visible:ring-red-500' : 'mt-1'}
                disabled={isLoading}
                required
                aria-required="true"
                aria-invalid={isPasswordRequiredError}
                aria-describedby={isPasswordRequiredError ? 'login-password-error' : undefined}
                autoComplete="current-password"
              />
              {isPasswordRequiredError && (
                <p id="login-password-error" className="text-sm text-red-600 mt-1" role="alert">
                  Contraseña es requerida
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
                <ExclamationCircleIcon width={18} height={18} />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary-700 text-white"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="mr-2 animate-spin" width={18} height={18} />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="mailto:soporte@migro.es"
              className="text-sm text-primary hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

